// =============================================================================
// Eqaya Platform - Comprehensive State Management Types
// =============================================================================

export enum ProjectStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  DISPUTED = 'disputed',
  CANCELLED = 'cancelled',
  PAUSED = 'paused'
}

export enum MilestoneStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  DISPUTED = 'disputed'
}

export enum PaymentStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  REFUNDED = 'refunded',
  DISPUTED = 'disputed'
}

export enum ContractorVerificationLevel {
  UNVERIFIED = 'unverified',
  BRONZE = 'bronze',
  SILVER = 'silver',
  GOLD = 'gold',
  PLATINUM = 'platinum'
}

export enum WebSocketConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error'
}

// =============================================================================
// Core Entity Interfaces
// =============================================================================

export interface User {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  country: string;
  profileImage?: string;
  isContractor: boolean;
  isInvestor: boolean;
  createdAt: string;
  updatedAt: string;
  preferences: UserPreferences;
}

export interface UserPreferences {
  currency: string;
  language: string;
  notifications: NotificationSettings;
  theme: 'light' | 'dark' | 'auto';
  timezone: string;
}

export interface NotificationSettings {
  email: boolean;
  sms: boolean;
  push: boolean;
  milestoneUpdates: boolean;
  paymentAlerts: boolean;
  projectMessages: boolean;
}

export interface Project {
  id: string;
  title: string;
  description: string;
  clientId: string;
  client: User;
  contractorId: string;
  contractor: Contractor;
  status: ProjectStatus;
  totalAmount: string; // In USDC
  releasedAmount: string; // In USDC
  currency: string;
  createdAt: string;
  updatedAt: string;
  startDate: string;
  estimatedEndDate: string;
  actualEndDate?: string;
  location: ProjectLocation;
  milestones: Milestone[];
  documents: ProjectDocument[];
  images: ProjectImage[];
  contractHash?: string; // IPFS hash
  riskScore: number; // 0-100
  progressPercentage: number;
  tags: string[];
  metadata: Record<string, any>;
}

export interface ProjectLocation {
  address: string;
  city: string;
  province: string;
  country: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  zipCode?: string;
}

export interface Milestone {
  id: string;
  projectId: string;
  title: string;
  description: string;
  amount: string; // In USDC
  status: MilestoneStatus;
  dueDate: string;
  completedAt?: string;
  approvedAt?: string;
  createdAt: string;
  updatedAt: string;
  proofOfWork: ProofOfWork[];
  dependencies: string[]; // Milestone IDs
  estimatedDuration: number; // In days
  actualDuration?: number; // In days
  notes?: string;
  metadata: Record<string, any>;
}

export interface ProofOfWork {
  id: string;
  type: 'image' | 'video' | 'document' | 'measurement';
  url: string;
  filename: string;
  uploadedAt: string;
  uploadedBy: string;
  description?: string;
  metadata: Record<string, any>;
}

export interface ProjectDocument {
  id: string;
  projectId: string;
  name: string;
  type: 'contract' | 'permit' | 'blueprint' | 'invoice' | 'other';
  url: string;
  ipfsHash?: string;
  uploadedAt: string;
  uploadedBy: string;
  size: number;
  mimeType: string;
  isPublic: boolean;
}

export interface ProjectImage {
  id: string;
  projectId: string;
  milestoneId?: string;
  url: string;
  thumbnailUrl: string;
  caption?: string;
  uploadedAt: string;
  uploadedBy: string;
  location?: ProjectLocation;
  metadata: Record<string, any>;
}

export interface Contractor {
  id: string;
  userId: string;
  user: User;
  companyName?: string;
  licenseNumber?: string;
  verificationLevel: ContractorVerificationLevel;
  specializations: string[];
  experienceYears: number;
  completedProjects: number;
  totalValueDelivered: string; // In USDC
  averageRating: number;
  reviewCount: number;
  location: ProjectLocation;
  portfolio: PortfolioItem[];
  certifications: Certification[];
  teamMembers: TeamMember[];
  availability: ContractorAvailability;
  hourlyRate?: string; // In USDC
  isActive: boolean;
  lastActiveAt: string;
  createdAt: string;
  updatedAt: string;
  metadata: Record<string, any>;
}

export interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  images: string[];
  completedAt: string;
  value: string; // In USDC
  duration: number; // In days
  tags: string[];
}

export interface Certification {
  id: string;
  name: string;
  issuer: string;
  issuedAt: string;
  expiresAt?: string;
  credentialUrl?: string;
  isVerified: boolean;
}

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  experience: number;
  specializations: string[];
  profileImage?: string;
}

export interface ContractorAvailability {
  isAvailable: boolean;
  availableFrom?: string;
  availableUntil?: string;
  workingHours: {
    start: string; // HH:mm format
    end: string; // HH:mm format
  };
  workingDays: number[]; // 0-6, where 0 = Sunday
  timeZone: string;
}

export interface Payment {
  id: string;
  projectId: string;
  milestoneId?: string;
  fromUserId: string;
  toUserId: string;
  amount: string; // In USDC
  currency: string;
  status: PaymentStatus;
  paymentMethod: PaymentMethod;
  transactionHash?: string;
  createdAt: string;
  completedAt?: string;
  failedAt?: string;
  refundedAt?: string;
  platformFee: string; // In USD
  notes?: string;
  metadata: Record<string, any>;
}

export interface PaymentMethod {
  type: 'mobile_money' | 'bank_transfer';
  provider?: string; // e.g., 'ecocash', 'onemoney', 'bank_name'
  accountDetails?: Record<string, any>;
  isDefault: boolean;
}

export interface Message {
  id: string;
  projectId: string;
  senderId: string;
  sender: User;
  recipientId: string;
  recipient: User;
  content: string;
  messageType: 'text' | 'image' | 'file' | 'system' | 'milestone_update';
  isRead: boolean;
  sentAt: string;
  readAt?: string;
  attachments: MessageAttachment[];
  metadata: Record<string, any>;
}

export interface MessageAttachment {
  id: string;
  filename: string;
  url: string;
  mimeType: string;
  size: number;
}

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  readAt?: string;
  actionUrl?: string;
  metadata: Record<string, any>;
}

export enum NotificationType {
  MILESTONE_COMPLETED = 'milestone_completed',
  MILESTONE_APPROVED = 'milestone_approved',
  PAYMENT_RECEIVED = 'payment_received',
  PAYMENT_SENT = 'payment_sent',
  PROJECT_CREATED = 'project_created',
  PROJECT_ASSIGNED = 'project_assigned',
  MESSAGE_RECEIVED = 'message_received',
  DISPUTE_RAISED = 'dispute_raised',
  VERIFICATION_COMPLETED = 'verification_completed',
  SYSTEM_ANNOUNCEMENT = 'system_announcement'
}

// =============================================================================
// State Interfaces
// =============================================================================

export interface ProjectState {
  projects: Record<string, Project>;
  currentProject: Project | null;
  userProjects: {
    asClient: string[];
    asContractor: string[];
  };
  loading: {
    fetchProjects: boolean;
    createProject: boolean;
    updateProject: boolean;
    deleteProject: boolean;
  };
  error: {
    fetchProjects: string | null;
    createProject: string | null;
    updateProject: string | null;
    deleteProject: string | null;
  };
  filters: ProjectFilters;
  pagination: PaginationState;
  lastFetched: string | null;
  cache: CacheState;
}

export interface ProjectFilters {
  status: ProjectStatus[];
  location: string;
  priceRange: {
    min: number;
    max: number;
  };
  dateRange: {
    start: string;
    end: string;
  };
  contractorId?: string;
  clientId?: string;
  tags: string[];
  searchQuery: string;
}

export interface ContractorState {
  contractors: Record<string, Contractor>;
  currentContractor: Contractor | null;
  searchResults: string[];
  loading: {
    fetchContractors: boolean;
    updateProfile: boolean;
    verifyContractor: boolean;
    searchContractors: boolean;
  };
  error: {
    fetchContractors: string | null;
    updateProfile: string | null;
    verifyContractor: string | null;
    searchContractors: string | null;
  };
  filters: ContractorFilters;
  pagination: PaginationState;
  lastFetched: string | null;
  cache: CacheState;
}

export interface ContractorFilters {
  verificationLevel: ContractorVerificationLevel[];
  specializations: string[];
  location: string;
  priceRange: {
    min: number;
    max: number;
  };
  rating: {
    min: number;
    max: number;
  };
  availability: boolean;
  experienceYears: {
    min: number;
    max: number;
  };
  searchQuery: string;
}

export interface PaymentState {
  payments: Record<string, Payment>;
  userPayments: {
    sent: string[];
    received: string[];
  };
  paymentMethods: PaymentMethod[];
  loading: {
    fetchPayments: boolean;
    processPayment: boolean;
    addPaymentMethod: boolean;
    refundPayment: boolean;
  };
  error: {
    fetchPayments: string | null;
    processPayment: string | null;
    addPaymentMethod: string | null;
    refundPayment: string | null;
  };
  pagination: PaginationState;
  lastFetched: string | null;
  cache: CacheState;
}

export interface WebSocketState {
  connectionStatus: WebSocketConnectionStatus;
  isConnected: boolean;
  lastPing: string | null;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  reconnectInterval: number; // milliseconds
  activeChannels: string[];
  messageQueue: WebSocketMessage[];
  events: WebSocketEvent[];
  error: string | null;
}

export interface WebSocketMessage {
  id: string;
  type: string;
  payload: any;
  timestamp: string;
  acknowledged: boolean;
  retryCount: number;
}

export interface WebSocketEvent {
  id: string;
  type: string;
  data: any;
  timestamp: string;
  processed: boolean;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  loading: {
    login: boolean;
    register: boolean;
    logout: boolean;
    refreshToken: boolean;
    updateProfile: boolean;
  };
  error: {
    login: string | null;
    register: string | null;
    logout: string | null;
    refreshToken: string | null;
    updateProfile: string | null;
  };
  lastLogin: string | null;
  sessionExpiry: string | null;
}

export interface NotificationState {
  notifications: Record<string, Notification>;
  unreadCount: number;
  loading: {
    fetchNotifications: boolean;
    markAsRead: boolean;
    deleteNotification: boolean;
  };
  error: {
    fetchNotifications: string | null;
    markAsRead: string | null;
    deleteNotification: string | null;
  };
  pagination: PaginationState;
  lastFetched: string | null;
}

export interface MessageState {
  messages: Record<string, Message>;
  conversations: Record<string, string[]>; // projectId -> messageIds
  unreadCounts: Record<string, number>; // projectId -> unread count
  loading: {
    fetchMessages: boolean;
    sendMessage: boolean;
    markAsRead: boolean;
  };
  error: {
    fetchMessages: string | null;
    sendMessage: string | null;
    markAsRead: string | null;
  };
  pagination: Record<string, PaginationState>; // per conversation
  lastFetched: Record<string, string>; // per conversation
}

export interface UIState {
  theme: 'light' | 'dark' | 'auto';
  sidebarOpen: boolean;
  currentPage: string;
  breadcrumbs: BreadcrumbItem[];
  modals: Record<string, boolean>;
  dialogs: Record<string, boolean>;
  snackbars: SnackbarState[];
  loading: {
    global: boolean;
    page: boolean;
  };
  error: {
    global: string | null;
    page: string | null;
  };
}

export interface BreadcrumbItem {
  label: string;
  path: string;
  icon?: string;
}

export interface SnackbarState {
  id: string;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
  duration: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

export interface CacheState {
  lastUpdated: string;
  ttl: number; // seconds
  version: string;
  etag?: string;
}

// =============================================================================
// Root Application State
// =============================================================================

export interface AppState {
  auth: AuthState;
  projects: ProjectState;
  contractors: ContractorState;
  payments: PaymentState;
  realTime: WebSocketState;
  notifications: NotificationState;
  messages: MessageState;
  ui: UIState;
}

// =============================================================================
// API Response Types
// =============================================================================

export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
  errors?: ValidationError[];
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  meta?: Record<string, any>;
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

// =============================================================================
// Action Types
// =============================================================================

export interface AsyncThunkConfig {
  state: AppState;
  rejectValue: string;
}

export interface LoadingState {
  [key: string]: boolean;
}

export interface ErrorState {
  [key: string]: string | null;
}

// =============================================================================
// Cache Configuration Types
// =============================================================================

export interface CacheConfig {
  redis?: {
    url: string;
    host?: string;
    port?: number;
    password?: string;
    db?: number;
  };
  memory?: {
    maxSize: number; // in MB
    ttl: number; // in seconds
  };
  strategies: CacheStrategy[];
  defaultTtl: number; // in seconds
  keyPrefix: string;
  compression: boolean;
  serialization: 'json' | 'msgpack';
}

export type CacheStrategy = 'memory' | 'redis' | 'localStorage' | 'sessionStorage';

export interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number;
  etag?: string;
  version: string;
  compressed?: boolean;
}

export interface CacheKey {
  namespace: string;
  identifier: string;
  version?: string;
  params?: Record<string, any>;
}